import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Search, Send, Loader2, CheckSquare, Square, X } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { CompanyLogo } from "@/components/company/CompanyLogo";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useI18n } from "@/lib/i18n/i18n";
import { api, apiError, assetUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { stripAccents } from "@/lib/demo/utils";

const norm = (s) => stripAccents(String(s || "")).toLowerCase();

const CompanyCard = ({ c, shared, selecting, selected, onToggle }) => {
  const { t } = useI18n();
  const canPick = selecting && !shared;
  const body = (
    <>
      <div className="relative h-44">
        <div className="h-full overflow-hidden"><img src={assetUrl(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
        {shared && <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white" data-testid={`company-card-shared-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("CV compartido")}</span>}
        {canPick && <span className={`absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full transition-colors ${selected ? "bg-upv text-white" : "bg-white/90 text-slate-500"}`} data-testid={`company-card-check-${c.id}`}>{selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}</span>}
        <div className="absolute bottom-0 left-5 translate-y-1/2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-md"><CompanyLogo id={c.id} logo={c.logo} size="text-lg" /></div>
      </div>
      <div className="px-6 pb-6 pt-10">
        <p className="font-display text-xl font-bold">{c.name}</p>
        <p className="mt-1 text-sm text-slate-500">{t(c.sector)}</p>
        <div className="mt-5 flex items-center justify-between text-xs font-semibold">
          <span className="flex items-center gap-1.5 text-slate-500"><MapPin className="h-3.5 w-3.5 text-upv" /> Stand {c.stand}</span>
          <span className="flex items-center gap-1.5 text-upv">{t("{n} oportunidades", { n: c.jobs.length })} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
        </div>
      </div>
    </>
  );
  const cls = `group overflow-hidden rounded-[28px] border bg-white text-left transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:shadow-xl ${selected ? "border-upv ring-2 ring-upv/30" : "border-slate-200"}`;
  if (canPick) return <button type="button" onClick={() => onToggle(c.id)} className={cls} data-testid={`company-card-${c.id}`}>{body}</button>;
  return <Link to={`/empresas/${c.id}`} className={cls} data-testid={`company-card-${c.id}`}>{body}</Link>;
};

const Filters = ({ q, setQ, sector, setSector, sectors, count }) => {
  const { t } = useI18n();
  return (
    <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar empresa u oferta")} className="h-12 rounded-xl bg-white pl-11" data-testid="companies-search-input" />
      </div>
      <Select value={sector} onValueChange={setSector}>
        <SelectTrigger className="h-12 w-full rounded-xl bg-white sm:w-[240px]" data-testid="companies-sector-filter"><SelectValue placeholder={t("Sector")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("Todos los sectores")}</SelectItem>
          {sectors.map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="shrink-0 text-sm font-semibold text-slate-500" data-testid="companies-count">{t("{n} empresas", { n: count })}</span>
    </div>
  );
};

const BulkPanel = ({ selecting, setSelecting, selected, visibleIds, setSelected, isParticipant }) => {
  const { t } = useI18n();
  if (!isParticipant) return <p className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-600" data-testid="companies-bulk-login-hint">{t("Inicia sesión como participante para enviar tu CV a varias empresas.")} <Link to="/login?next=/empresas" className="font-semibold text-upv hover:underline">{t("INICIAR SESIÓN")}</Link></p>;
  return (
    <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-upv/20 bg-upv-light/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid="companies-bulk-panel">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Envío online abierto")}</p>
        <p className="mt-1 text-sm text-slate-700">{t("Selecciona las empresas y envíales tu currículum de una sola vez.")}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {selecting && <button onClick={() => setSelected(new Set(visibleIds))} className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-xs font-bold" data-testid="companies-select-all">{t("Seleccionar todas las visibles")}</button>}
        <button onClick={() => { setSelecting(!selecting); setSelected(new Set()); }} className={`h-10 rounded-xl px-4 text-xs font-bold ${selecting ? "border border-slate-300 bg-white" : "bg-upv text-white hover:bg-upv-dark"}`} data-testid="companies-select-toggle">{t(selecting ? "Terminar selección" : "Seleccionar empresas")}</button>
      </div>
    </div>
  );
};

const SendBar = ({ count, onClear, onSend }) => {
  const { t } = useI18n();
  if (!count) return null;
  return (
    <div className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-fit max-w-[calc(100vw-24px)] items-center gap-3 rounded-full bg-slate-950 p-2 pl-5 text-white shadow-2xl" data-testid="companies-send-bar">
      <span className="text-sm font-semibold" data-testid="companies-selected-count">{t(count === 1 ? "1 seleccionada" : "{n} seleccionadas", { n: count })}</span>
      <button onClick={onClear} title={t("Quitar selección")} className="rounded-full p-2 text-white/60 hover:bg-white/10 hover:text-white" data-testid="companies-clear-selection"><X className="h-4 w-4" /></button>
      <button onClick={onSend} className="flex h-11 items-center gap-2 rounded-full bg-upv px-5 text-sm font-bold hover:bg-upv-dark" data-testid="companies-send-button"><Send className="h-4 w-4" /> {t(count === 1 ? "Enviar CV a 1 empresa" : "Enviar CV a {n} empresas", { n: count })}</button>
    </div>
  );
};

export default function Empresas() {
  const { user, settings } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [companies, setCompanies] = useState(null);
  const [shared, setShared] = useState(new Set());
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const isParticipant = user?.role === "participant";

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data.items)).catch(() => setCompanies([]));
    if (params.get("qr") === "invalid") toast.error(t("QR de empresa no válido"));
  }, [params, t]);
  const loadShared = () => api.get("/participants/me/activity").then((r) => setShared(new Set(r.data.items.filter((i) => i.kind === "cv").map((i) => i.companySlug)))).catch(() => {});
  useEffect(() => { if (isParticipant) loadShared(); else setShared(new Set()); }, [isParticipant]);

  const sectors = useMemo(() => [...new Set((companies || []).map((c) => c.sector).filter(Boolean))].sort(), [companies]);
  const visible = useMemo(() => {
    const nq = norm(q.trim());
    return (companies || []).filter((c) => (sector === "all" || c.sector === sector) && (!nq || norm([c.name, c.sector, c.desc, ...c.jobs.map((j) => j[0])].join(" ")).includes(nq)));
  }, [companies, q, sector]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const visibleIds = visible.filter((c) => !shared.has(c.id)).map((c) => c.id);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/companies/apply-bulk", { slugs: [...selected] });
      toast.success(t(data.received === 1 ? "CV enviado a 1 empresa" : "CV enviado a {n} empresas", { n: data.received }), data.duplicate ? { description: t("{n} ya lo tenían", { n: data.duplicate }) } : undefined);
      setSelected(new Set()); setSelecting(false); setConfirm(false); loadShared();
    } catch (e) { toast.error(t(apiError(e).message)); } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="companies-page">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-5 py-12 lg:px-10 lg:py-16">
        <p className="fade-up text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Foro de Empleo UPV 2027 · 12–13 mayo")}</p>
        <h1 className="fade-up-1 mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="companies-title">{t("Empresas participantes")}</h1>
        <p className="fade-up-2 mt-4 max-w-2xl text-slate-600">{t("Descubre las empresas y sus oportunidades, y comparte tu currículum antes, durante o después de visitar su stand.")}</p>
        <Link to="/empresas/inscripcion" className="fade-up-2 mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-upv underline-offset-4 hover:underline" data-testid="companies-register-link">{t("Inscripción de empresas")} <ArrowRight className="h-3.5 w-3.5" /></Link>
        {settings.postEventOpen && <BulkPanel {...{ selecting, setSelecting, selected, visibleIds, setSelected, isParticipant }} />}
        <Filters q={q} setQ={setQ} sector={sector} setSector={setSector} sectors={sectors} count={visible.length} />
        {companies === null && <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-[28px] bg-slate-200/60" />)}</div>}
        {companies?.length === 0 && <p className="mt-8 text-sm text-slate-500" data-testid="companies-empty">{t("Aún no hay empresas publicadas.")}</p>}
        {companies?.length > 0 && visible.length === 0 && <p className="mt-8 text-sm text-slate-500" data-testid="companies-no-match">{t("Ninguna empresa coincide con la búsqueda.")}</p>}
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((c) => <CompanyCard key={c.id} c={c} shared={shared.has(c.id)} selecting={selecting && isParticipant && settings.postEventOpen} selected={selected.has(c.id)} onToggle={toggle} />)}
        </div>
      </main>
      <PageFooter />
      {selecting && <SendBar count={selected.size} onClear={() => setSelected(new Set())} onSend={() => setConfirm(true)} />}
      <AlertDialog open={confirm} onOpenChange={(o) => !sending && setConfirm(o)}>
        <AlertDialogContent className="rounded-[28px]" data-testid="companies-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold">{t(selected.size === 1 ? "¿Enviar tu perfil y CV a 1 empresa?" : "¿Enviar tu perfil y CV a {n} empresas?", { n: selected.size })}</AlertDialogTitle>
            <AlertDialogDescription>{t("Cada empresa podrá ver tu perfil profesional y descargar tu currículum.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-slate-700">{(companies || []).filter((c) => selected.has(c.id)).map((c) => <li key={c.id}>· {c.name}</li>)}</ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="companies-bulk-cancel">{t("Cancelar")}</AlertDialogCancel>
            <button onClick={send} disabled={sending} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-upv px-5 text-sm font-bold text-white hover:bg-upv-dark disabled:opacity-70" data-testid="companies-bulk-confirm">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Enviar CV")}</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
